'use client';

import { NavContainer, PrimaryNav } from '@petsmart-ui/sparky';
import { type MouseEvent, useEffect, useState } from 'react';

import { dataLayerPush, getBaseUrlJoined } from '@libs/analytics';
import { usePageLayout, useStore } from '@libs/common-next';
import { isUserLoggedIn } from '@libs/core';
import { handleServiceClick } from '@libs/experience-api';

import { propsMapper } from '../../utils/propsMapper';
import { LegacyMyStore } from '../LegacyMyStore/LegacyMyStore';
import { SubMenu } from './SubMenu';
import { type CategoryTypes, type MainMenuProps } from './types';
import { trackingData } from './utils';
import { handleNavLink } from './utils/handleNavLink';

import './MainMenu.scss';
import './MainMenuLayouts.scss';
import './MainMenuLegacy.scss';
import './MainMenuLegacyLayouts.scss';

export function MainMenu({ category: menuCategories }: Readonly<MainMenuProps>) {
    const { isAuthenticated, isSoftAuthenticated } = usePageLayout();
    const { selectedStore } = useStore();
    const { data: store } = selectedStore;
    const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);
    const [menuTexts, setMenuTexts] = useState<Record<string, string>>({});

    /**
     * TODO: [SB-1434] Primary nav classes switch on Optimizely `exp_header_footer` (`mainmenu__*` vs
     * `legacy-mainmenu__*`). Legacy styling stayed default for deployment stability until the new bar
     * fully rolls out.
     */

    const navClasses = {
        container: 'legacy-mainmenu__container',
        primaryNav: 'legacy-mainmenu__primary-nav',
    };

    const mappedCategories = propsMapper<CategoryTypes>(menuCategories).filter(c => c.sysId);

    useEffect(() => {
        const newMenuTexts: { [key: string]: string } = {};

        mappedCategories.forEach((mappedCategory: CategoryTypes) => {
            const { sysId, type = 'text', displayText } = mappedCategory;
            const isMyStore = type === 'my-store';

            // @ts-expect-error need to use ts-ignore to delete error for sysId being string | undefined (There is no possibility for it to be undefined, error is thrown because of Partial type used on CategoryTypes type )
            newMenuTexts[sysId] = !isUserLoggedIn() && isMyStore ? 'Find a Store' : (displayText as string);
        });

        setMenuTexts(newMenuTexts);
    }, [isAuthenticated, store]);

    return (
        <NavContainer data-testid="main-navigation" className={navClasses.container}>
            <PrimaryNav
                className={navClasses.primaryNav}
                onMouseEnter={() => setIsSubMenuVisible(true)}
                id="primary-nav"
                data-testid="primary-nav"
            >
                {mappedCategories.map((mappedCategory: CategoryTypes) => {
                    const {
                        sysId,
                        type = 'text',
                        displayText,
                        link,
                        specialType,
                        categories,
                        highlighted,
                        menuDropdownStyle = false,
                    } = mappedCategory;

                    const isMyStore = type === 'my-store';
                    const emptyStore = isMyStore && !store;
                    // @ts-expect-error need to use ts-ignore to delete error for sysId being string | undefined (There is no possibility for it to be undefined, error is thrown because of Partial type used on CategoryTypes type )
                    const currentText = menuTexts[sysId] || displayText;

                    const { href, text } = handleNavLink(type, currentText, link, store);

                    function handleOnClick(event: MouseEvent) {
                        dataLayerPush(
                            trackingData({
                                selection: displayText,
                                link_url: getBaseUrlJoined(href),
                            })
                        );

                        handleServiceClick({
                            authType: isAuthenticated,
                            event,
                            link: href,
                            isSoftAuthenticated,
                        });
                    }

                    return (
                        <PrimaryNav.Item
                            tabIndex={-1}
                            className={highlighted ? 'highlighted' : ''}
                            href={href}
                            text={text}
                            key={sysId}
                            onNavItemClick={handleOnClick}
                            id={displayText?.toLowerCase().split(' ').join('-')}
                        >
                            {
                                // isSubMenuVisible:
                                // This conditional rendering is necessary to ensure that SubMenu Components are only rendered when the user needs them,
                                // this reduces the initial load of the layout and therefore reducing TBT (Performance Metric)
                                !emptyStore && isSubMenuVisible && (
                                    <>
                                        {(categories || type === 'card') && (
                                            <SubMenu
                                                mappedCategory={mappedCategory}
                                                specialType={specialType}
                                                path={mappedCategory.displayText ? [mappedCategory.displayText] : []}
                                                type={type}
                                                parentCategory={displayText}
                                                menuDropdownStyle={menuDropdownStyle}
                                            />
                                        )}
                                        {isMyStore && (
                                            <ul className="submenu-container__list">
                                                <LegacyMyStore />
                                            </ul>
                                        )}
                                    </>
                                )
                            }
                        </PrimaryNav.Item>
                    );
                })}
            </PrimaryNav>
        </NavContainer>
    );
}
